import { useNavigate } from "react-router";
import EditAlertIcon from "@/assets/icons/edit_notifications.svg?react";
import AskVivoIcon from "@/assets/icons/ask_vivo2.svg?react";
import EditIcon from "@/assets/icons/edit_pencil.svg?react";
import ArchiveIcon from "@/assets/icons/archive.svg?react";
import ActivateIcon from "@/assets/icons/archive_action.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import DeleteAlertIcon from "@/assets/icons/delete_alert.svg?react";
import AddIcon from "@/assets/icons/add.svg?react";
import SharedIcon from "@/assets/icons/SharedIcon.svg?react";
import { InsightProps } from "./insights";
import { isInsightArchived } from "./utils";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useSuperuserMode } from "@/contexts/useSuperuser";

export type InsightMenuItemsProps = {
  insight: InsightProps;
  onOpenEditModal: () => void;
  onArchiveInsight: () => void;
  onOpenDeleteAlertModal?: () => void;
  onOpenDeleteModal: () => void;
  onActivateInsight: () => void;
  onOpenAddAlertModal: () => void;
  onOpenShareModal?: () => void;
};

export const useInsightMenuItems = ({
  insight,
  onOpenEditModal,
  onArchiveInsight,
  onOpenDeleteModal,
  onOpenDeleteAlertModal,
  onActivateInsight,
  onOpenAddAlertModal,
  onOpenShareModal,
}: InsightMenuItemsProps) => {
  const navigate = useNavigate();
  const { buildUrl } = useScopeParams();
  const { isSuperuserMode } = useSuperuserMode();

  if (!insight) return {};

  const baseItems = [
    {
      key: "1",
      label: (
        <div
          data-testid="insight-card-menu-item-ask-vivo"
          className="flex items-center gap-2 text-[#2B4752]"
          onClick={() =>
            navigate(buildUrl("askvivo"), {
              state: { question: insight.av_question },
            })
          }
        >
          <AskVivoIcon className="w-6 h-6" />
          Ask Vivo
        </div>
      ),
    },
  ];

  if (!isInsightArchived(insight)) {
    const activeItems = [];

    if (insight.is_shared) {
      if (isSuperuserMode) {
        activeItems.push({
          key: "2",
          label: (
            <div
              data-testid={
                insight.alert?.id
                  ? "insight-card-menu-item-rename-alert"
                  : "insight-card-menu-item-add-alert"
              }
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onOpenAddAlertModal();
              }}
            >
              {insight.alert?.id ? (
                <>
                  <EditAlertIcon className="w-6 h-6" />
                  Rename alert
                </>
              ) : (
                <>
                  <AddIcon className="w-6 h-6" />
                  Turn into alert
                </>
              )}
            </div>
          ),
        });
        activeItems.push({
          key: "3",
          label: (
            <div
              data-testid="insight-card-menu-item-edit-insight"
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onOpenEditModal();
              }}
            >
              <EditIcon className="w-6 h-6" />
              Edit insight
            </div>
          ),
        });
        activeItems.push({
          key: "7",
          label: (
            <div
              data-testid="insight-card-menu-item-unshare-insight"
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onOpenShareModal?.();
              }}
            >
              <SharedIcon className="w-6 h-6" />
              Unshare Insight
            </div>
          ),
        });

        activeItems.push({
          key: "4",
          label: (
            <div
              data-testid="insight-card-menu-item-archive"
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onArchiveInsight();
              }}
            >
              <ArchiveIcon className="w-5 h-5" />
              <span className="ml-1">Archive</span>
            </div>
          ),
        });

        if (insight.alert?.id) {
          activeItems.push({
            key: "5",
            label: (
              <div
                data-testid="insight-card-menu-item-delete-alert"
                className="flex items-center gap-2 text-[#2B4752]"
                onClick={() => {
                  onOpenDeleteAlertModal?.();
                }}
              >
                <DeleteAlertIcon className="w-5.5 h-5.5" />
                Delete alert
              </div>
            ),
          });
        }

        activeItems.push({
          key: "6",
          label: (
            <div
              data-testid="insight-card-menu-item-delete"
              className="flex items-center gap-2 text-[#980000]"
              onClick={() => {
                onOpenDeleteModal();
              }}
            >
              <DeleteIcon className="w-6 h-6" />
              Delete
            </div>
          ),
        });
      }
    } else {
      activeItems.push({
        key: "2",
        label: (
          <div
            data-testid={
              insight.alert?.id
                ? "insight-card-menu-item-rename-alert"
                : "insight-card-menu-item-add-alert"
            }
            className="flex items-center gap-2 text-[#2B4752]"
            onClick={() => {
              onOpenAddAlertModal();
            }}
          >
            {insight.alert?.id ? (
              <>
                <EditAlertIcon className="w-6 h-6" />
                Rename alert
              </>
            ) : (
              <>
                <AddIcon className="w-6 h-6" />
                Turn into alert
              </>
            )}
          </div>
        ),
      });
      activeItems.push({
        key: "3",
        label: (
          <div
            data-testid="insight-card-menu-item-edit-insight"
            className="flex items-center gap-2 text-[#2B4752]"
            onClick={() => {
              onOpenEditModal();
            }}
          >
            <EditIcon className="w-6 h-6" />
            Edit insight
          </div>
        ),
      });

      if (isSuperuserMode) {
        activeItems.push({
          key: "7",
          label: (
            <div
              data-testid="insight-card-menu-item-share-insight"
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onOpenShareModal?.();
              }}
            >
              <SharedIcon className="w-6 h-6" />
              Share Insight
            </div>
          ),
        });
      }

      activeItems.push({
        key: "4",
        label: (
          <div
            data-testid="insight-card-menu-item-archive"
            className="flex items-center gap-2 text-[#2B4752]"
            onClick={() => {
              onArchiveInsight();
            }}
          >
            <ArchiveIcon className="w-5 h-5" />
            <span className="ml-1">Archive</span>
          </div>
        ),
      });

      if (insight.alert?.id) {
        activeItems.push({
          key: "5",
          label: (
            <div
              data-testid="insight-card-menu-item-delete-alert"
              className="flex items-center gap-2 text-[#2B4752]"
              onClick={() => {
                onOpenDeleteAlertModal?.();
              }}
            >
              <DeleteAlertIcon className="w-5.5 h-5.5" />
              Delete alert
            </div>
          ),
        });
      }

      activeItems.push({
        key: "6",
        label: (
          <div
            data-testid="insight-card-menu-item-delete"
            className="flex items-center gap-2 text-[#980000]"
            onClick={() => {
              onOpenDeleteModal();
            }}
          >
            <DeleteIcon className="w-6 h-6" />
            Delete
          </div>
        ),
      });
    }

    return [...baseItems, ...activeItems];
  }

  const inactiveItems = [
    {
      key: "2",
      label: (
        <div
          data-testid="insight-card-menu-item-activate"
          className="flex items-center gap-2 text-[#2B4752]"
          onClick={() => {
            onActivateInsight();
          }}
        >
          <ActivateIcon />
          Activate
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          data-testid="insight-card-menu-item-delete"
          className="flex items-center gap-2 text-[#2B4752]"
          onClick={() => {
            onOpenDeleteModal();
          }}
        >
          <DeleteIcon />
          Delete
        </div>
      ),
    },
  ];

  return [...baseItems, ...inactiveItems];
};
